<script lang="ts">
	/**
	 * The numeric sibling of `StringListEditor`: a list of whole numbers as a text field plus a row
	 * of removable chips.
	 *
	 * Two differences from the string editor, and both are the reason this is a separate component
	 * rather than a `numeric` prop on that one:
	 *
	 * - **Entries are coerced to numbers**, so the list submits `[10, 60]` and not `["10", "60"]`.
	 *   Round-tripping as strings is silent: the values look right in the UI, and the declared
	 *   `min`/`max` on the server then reject the whole batch — or worse, a schema that accepts
	 *   strings applies no range at all.
	 * - **Duplicates are allowed.** `webhooks.retryScheduleSec` ends `…, 86400, 86400, 86400` on
	 *   purpose — one entry per attempt — and the string editor's case-insensitive
	 *   duplicate rejection would quietly refuse the default value of a shipped setting.
	 */

	import { Badge, Button, Input } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';

	let cssClass: string = '';
	export { cssClass as class };

	/** Two-way bindable list of values. */
	export let values: number[] = [];

	/** Declared element range, from the setting descriptor. Null means unbounded on that side. */
	export let minimum: number | null = null;
	export let maximum: number | null = null;

	export let placeHolder: string = 'Add a number';
	export let disabled: boolean = false;
	export let id: string = '';
	export let size: 'sm' | 'md' | 'lg' | undefined = 'md';

	const dispatch = createEventDispatcher<{ onChange: { values: number[] } }>();

	let newEntry: string = '';
	/** Why the last entry was refused. Shown inline rather than as a toast: it is about this field. */
	let entryError: string = '';

	/** Exported so a parent can state the rule once, next to the field, rather than guessing it. */
	export function describeRange(): string {
		if (minimum !== null && maximum !== null) {
			return `Whole numbers between ${minimum} and ${maximum}.`;
		}
		if (minimum !== null) {
			return `Whole numbers of ${minimum} or more.`;
		}
		if (maximum !== null) {
			return `Whole numbers of ${maximum} or less.`;
		}
		return 'Whole numbers.';
	}

	const fnAddEntry = (event: SubmitEvent): void => {
		event.preventDefault();
		const trimmed = newEntry.trim();
		if (trimmed.length === 0) return;

		const parsed = Number(trimmed);
		if (!Number.isInteger(parsed)) {
			entryError = `"${trimmed}" is not a whole number.`;
			return;
		}
		if (minimum !== null && parsed < minimum) {
			entryError = `${parsed} is below the minimum of ${minimum}.`;
			return;
		}
		if (maximum !== null && parsed > maximum) {
			entryError = `${parsed} is above the maximum of ${maximum}.`;
			return;
		}

		values = [...(values ?? []), parsed];
		newEntry = '';
		entryError = '';
		dispatch('onChange', { values });
	};

	const fnRemoveEntry = (index: number): void => {
		values = values.filter((_value, position) => position !== index);
		dispatch('onChange', { values });
	};
</script>

<div class={cssClass}>
	<form on:submit={fnAddEntry}>
		<Input type="text" inputmode="numeric" {id} {size} bind:value={newEntry} placeholder={placeHolder} {disabled} />
	</form>

	{#if entryError.length > 0}
		<p class="text-xs text-red-600 dark:text-red-500 mt-1">{entryError}</p>
	{/if}

	{#if values?.length > 0}
		<div class="flex flex-wrap gap-2 mt-2">
			<!-- Keyed by position, not by value: the list may legitimately repeat a number. -->
			{#each values as value, index (index)}
				<Badge color="blue">
					{value}
					{#if !disabled}
						<Button class="!p-0 px-0 ml-1 hover:text-white" color="none" title="Remove {value}" on:click={() => fnRemoveEntry(index)}>
							<i class="fa-solid fa-xmark" />
						</Button>
					{/if}
				</Badge>
			{/each}
		</div>
	{/if}
</div>
